import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import {
  isMobileApp,
  MobileLocations,
  navigateTo,
  openAuthenticatedUrl,
  sendMobileFullLogoutNotification,
} from '@ecs-uk/web-common-mobile-view-helpers';
import dayjs from 'dayjs';

import Analytics, { InteractionInputEvent, MilestoneInputEvent } from '@ecs-uk/web-common-analytics';
import { AnimatedLogo } from '@experian-uk/corvetteuk-common-ui';
import { CancellationType, Product, PlanType } from '../../constants/deactivate';
import products, { normalizeProductFamily } from '../../constants/products';
import familyName from '../../constants/familyName';
import { channels } from '../../constants';
import deactivateFulfillment from '../../actions/fulfillments/deactivate';
import setCurrentUser from '../../actions/auth/setCurrentUser';
import { getEnv } from '../Context/env';
import isServer from '../../helpers/isServer';
import usePageView from '../../hooks/usePageView';

import { DEACTIVATION_STAGES, DeactivationStage } from './constants';
import ChoiceCancelStage from './stages/ChoiceCancelStage';
import ConfirmCancelStage from './stages/ConfirmCancelStage';
import LoadingStage from './stages/LoadingStage';
import ErrorScreen from '../ErrorScreen';
import ReasonCaptureStage, { DeactivationReasonData } from './stages/ReasonCapture';
import { useGetEntitlementsQuery, useDeactivateFlexUserMutation } from '../../services/entitlementsApi';
import { FullPageContainer } from '../Container';
import {
  DeactivateWrapper,
  DeactivateContainer,
  DeactivateContentContainer,
  ContentCard,
  BackLinkWrapper,
} from './index.styles';

interface ResponseItem {
  status?: boolean;
  subscriptionId?: string;
}

interface FulfillmentResponse {
  status?: boolean;
  subscriptionId?: string;
  data?: ResponseItem | ResponseItem[];
}

type DeactivationApiResponse = ResponseItem[] | FulfillmentResponse | null;

interface DeactivateState {
  subscriptions?: {
    current?: {
      family?: string;
      isCoolingOffEligible?: boolean;
      copyLink?: string;
      productName?: string;
      defermentInfo?: unknown;
      planCode?: string | null;
    };
  };
  billing?: {
    nextBillingDate?: string;
  };
  // Placeholder store path for flex deactivation data. Replace with final state shape when BE wiring lands.
  flexDeactivation?: {
    current?: {
      family?: string;
      isCoolingOffEligible?: boolean;
      productName?: string;
      hasDeferment?: boolean;
      nextBillingDate?: string;
      planCode?: string | null;
    };
  };
}

interface DeactivateOrchestratorProps {
  isWebview?: boolean;
  useFlexFlow?: boolean;
}

interface DeactivationInput {
  family?: string;
  isCoolingOffEligible?: boolean;
  productName?: string;
  hasDeferment?: boolean;
  nextBillingDate?: string;
  planCode?: string | null;
}

const getProduct = (isIdentityPlus: boolean, isCreditExpert: boolean): Product => {
  if (isIdentityPlus) return Product.IdentityPlus;
  if (isCreditExpert) return Product.CreditExpert;
  return Product.Free;
};

const shouldLogoutUser = (response: FulfillmentResponse | ResponseItem[]): boolean => {
  let responseItems: ResponseItem[] = [];

  if (Array.isArray(response)) {
    responseItems = response;
  } else if (Array.isArray(response.data)) {
    responseItems = response.data;
  } else {
    responseItems = [response.data || response];
  }

  return responseItems.some(item => item?.status === true || !!item?.subscriptionId);
};

const getProfileRedirectUrl = (profileUrl: string): string | null => {
  if (!profileUrl) return null;

  try {
    const parsedProfileUrl = new URL(profileUrl);
    parsedProfileUrl.searchParams.set('reason', 'account_deactivated');
    return parsedProfileUrl.toString();
  } catch {
    return null;
  }
};

const getInitialStage = (
  isCoolingOffEligible: boolean,
  isBasicSubscription: boolean,
  hasExistingDeferment: boolean
): DeactivationStage => {
  if (isBasicSubscription || !isCoolingOffEligible) {
    return DEACTIVATION_STAGES.REASON;
  }
  if (hasExistingDeferment) {
    return DEACTIVATION_STAGES.REASON;
  }
  return DEACTIVATION_STAGES.CHOICE;
};

const getInitialCancellationType = (isBasicSubscription: boolean, hasExistingDeferment: boolean): CancellationType => {
  if (!isBasicSubscription && hasExistingDeferment) {
    return CancellationType.Deferred;
  }
  return null;
};

const getLoadingMessage = ({
  isBasicSubscription,
  isCoolingOffEligible,
  cancellationType,
  isDeferred,
}: {
  isBasicSubscription: boolean;
  isCoolingOffEligible: boolean;
  cancellationType: CancellationType;
  isDeferred: boolean;
}): string => {
  if (isBasicSubscription) {
    return 'Deactivating account';
  }

  if (isDeferred) {
    return 'Scheduling deactivation';
  }

  if (
    (isCoolingOffEligible && cancellationType === CancellationType.Deferred) ||
    (!isCoolingOffEligible && !isBasicSubscription)
  ) {
    return 'Cancelling subscription and scheduling deactivation';
  }

  return 'Cancelling subscription and deactivating account';
};

const getSubscriptionType = (isBasic: boolean, isIdentityPlus: boolean): string => {
  if (isBasic) return 'free';
  return isIdentityPlus ? 'identity' : 'credit_expert';
};

const DeactivateStages: FC<DeactivateOrchestratorProps> = ({ isWebview = false, useFlexFlow = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const env = getEnv();
  const { REACT_APP_PUBLIC_HOST: mySubscriptionsUrl, REACT_APP_PROFILE_URL: profileRedirectUrl } = env;
  const { data: entitlementsData } = useGetEntitlementsQuery(undefined);
  const [deactivateFlexUser] = useDeactivateFlexUserMutation();
  usePageView('ProdmoveDeactivatePage');

  const deactivationInput = useSelector((state: DeactivateState): DeactivationInput => {
    if (useFlexFlow) {
      const flexSubscription = entitlementsData;

      return {
        family: normalizeProductFamily(flexSubscription?.plans?.[0]?.planCode ?? ''),
        isCoolingOffEligible: flexSubscription?.isInCoolingOffPeriod,
        hasDeferment: !!flexSubscription?.defermentInfo,
        nextBillingDate: flexSubscription?.plans?.[0].nextBillDate ?? undefined,
        planCode: flexSubscription?.plans?.[0]?.planCode,
      };
    }

    const legacySubscription = state.subscriptions?.current;

    return {
      family: legacySubscription?.family,
      isCoolingOffEligible: legacySubscription?.isCoolingOffEligible,
      productName: legacySubscription?.productName,
      hasDeferment: !!legacySubscription?.defermentInfo,
      nextBillingDate: state.billing?.nextBillingDate,
      planCode: legacySubscription?.planCode,
    };
  });

  const currentSubscription = {
    family: deactivationInput.family,
    isCoolingOffEligible: deactivationInput.isCoolingOffEligible,
    productName: deactivationInput.productName,
    defermentInfo: deactivationInput.hasDeferment ? {} : null,
  };
  const nextBillingDate = deactivationInput.nextBillingDate;
  const isCoolingOffEligible = !!deactivationInput.isCoolingOffEligible;
  const isDeferred = !!deactivationInput.hasDeferment;

  const formattedNextBillingDate = nextBillingDate ? dayjs(nextBillingDate).format('D MMMM YYYY') : '';
  const isBasicSubscription = currentSubscription?.family === products.basic;
  const isIdentityPlusSubscription = currentSubscription?.family === products.identity;
  const isCreditExpertSubscription = currentSubscription?.family === products.credit_expert;

  const planType = isBasicSubscription ? PlanType.Free : PlanType.Paid;
  const product = getProduct(isIdentityPlusSubscription, isCreditExpertSubscription);

  const [currentStage, setCurrentStage] = useState<DeactivationStage>(() =>
    getInitialStage(!!isCoolingOffEligible, isBasicSubscription, isDeferred)
  );
  const [cancellationType, setCancellationType] = useState<CancellationType>(() =>
    getInitialCancellationType(isBasicSubscription, isDeferred)
  );
  const [deactivationReason, setDeactivationReason] = useState<DeactivationReasonData>([]);

  const immediateCancellationType = deactivationInput?.planCode ? CancellationType.Immediate : null;
  const effectiveCancellationType = planType === PlanType.Free ? immediateCancellationType : cancellationType;
  const loadingMessage = getLoadingMessage({
    isBasicSubscription,
    isCoolingOffEligible: !!isCoolingOffEligible,
    cancellationType,
    isDeferred,
  });

  const productDisplayName =
    currentSubscription?.productName ||
    (currentSubscription?.family
      ? familyName[currentSubscription.family as keyof typeof familyName]
      : 'your subscription');

  const handleBackClick = useCallback((): void => {
    if (isMobileApp()) {
      navigateTo(MobileLocations.BACK_TO_PERSONAL_DETAILS);
      return;
    }
    if (!isServer() && globalThis.location && profileRedirectUrl) {
      globalThis.location.href = profileRedirectUrl;
    }
  }, [profileRedirectUrl, currentStage]);

  const handleMySubscriptionClick = useCallback((): void => {
    if (isWebview) {
      if (mySubscriptionsUrl) {
        openAuthenticatedUrl(new URL(mySubscriptionsUrl));
      }
      return;
    }
    history.push('/');
  }, [history, isWebview, mySubscriptionsUrl]);

  const handleSelectDeferred = useCallback((): void => {
    Analytics.publish(
      InteractionInputEvent.fromObject({ component: 'deactivate_deferred', action: 'click', status: 'success' })
    );
    setCancellationType(CancellationType.Deferred);
    setCurrentStage(DEACTIVATION_STAGES.REASON);
  }, []);

  const handleSelectImmediate = useCallback((): void => {
    Analytics.publish(
      InteractionInputEvent.fromObject({ component: 'deactivate_immediate', action: 'click', status: 'success' })
    );
    setCancellationType(CancellationType.Immediate);
    setCurrentStage(DEACTIVATION_STAGES.REASON);
  }, []);

  const handleReasonContinue = useCallback((data: DeactivationReasonData): void => {
    setDeactivationReason(data);
    setCurrentStage(DEACTIVATION_STAGES.CONFIRM);
  }, []);

  const handleBackToPersonalDetails = useCallback((): void => {
    if (isMobileApp()) {
      navigateTo(MobileLocations.BACK_TO_PERSONAL_DETAILS);
      return;
    }
    if (!isServer() && globalThis.location && profileRedirectUrl) {
      globalThis.location.href = profileRedirectUrl;
    }
  }, [profileRedirectUrl]);

  const forceLogout = useCallback((): void => {
    dispatch(setCurrentUser());

    if (isMobileApp()) {
      sendMobileFullLogoutNotification();
      return;
    }

    if (!isServer() && globalThis.location) {
      const logoutUrl = new URL(env.REACT_APP_LOGOUT_URL, globalThis.location.origin);
      logoutUrl.searchParams.set('reason', 'account_deactivated');
      globalThis.location.href = logoutUrl.toString();
    }
  }, [dispatch, env.REACT_APP_LOGOUT_URL]);

  const handleConfirmDeactivation = useCallback(async (): Promise<void> => {
    Analytics.publish(
      InteractionInputEvent.fromObject({ component: 'confirm_deactivation', action: 'click', status: 'success' })
    );

    deactivationReason.forEach(({ reason, additionalComment }) => {
      Analytics.publish(
        MilestoneInputEvent.fromObject({
          deactivation_reason_selected: {
            reason,
            additionalContext: additionalComment,
          },
        })
      );
    });

    setCurrentStage(DEACTIVATION_STAGES.LOADING);

    const isImmediate =
      isBasicSubscription || (!!isCoolingOffEligible && cancellationType === CancellationType.Immediate);
    const subscriptionType = getSubscriptionType(isBasicSubscription, isIdentityPlusSubscription);
    const deactivationType = isImmediate ? 'immediate' : 'deferred';
    const deactivationFormattedDate = nextBillingDate ? dayjs(nextBillingDate).format('YYYY/MM/DD') : '';
    const deactivationDate = isImmediate ? dayjs().format('YYYY/MM/DD') : deactivationFormattedDate;

    const publishMilestone = (interactionStatus: boolean) => {
      Analytics.publish(
        MilestoneInputEvent.fromObject({
          confirm_deactivation: {
            subscription_type: subscriptionType,
            deactivation_date: deactivationDate,
            interaction_status: interactionStatus,
            deactivation_type: deactivationType,
            isCoolingOffEligible: !!isCoolingOffEligible,
          },
        })
      );
    };

    try {
      let response: DeactivationApiResponse;
      if (useFlexFlow) {
        response = (await deactivateFlexUser({
          CancellationType: effectiveCancellationType ?? CancellationType.Deferred,
          Channel: isWebview ? channels.app : channels.online,
          CancellationReason: '',
        }).unwrap()) as DeactivationApiResponse;
      } else {
        const dispatchResult = dispatch(
          deactivateFulfillment(effectiveCancellationType)
        ) as unknown as Promise<DeactivationApiResponse>;
        response = await dispatchResult;
      }
      const isSuccessfulDeactivation = response === null || shouldLogoutUser(response);

      publishMilestone(isSuccessfulDeactivation);

      const shouldRedirectToProfile =
        !isBasicSubscription &&
        isSuccessfulDeactivation &&
        ((isCoolingOffEligible && cancellationType === CancellationType.Deferred) || !isCoolingOffEligible);

      if (shouldRedirectToProfile) {
        if (isMobileApp()) {
          navigateTo(MobileLocations.PERSONAL_DETAILS_WITH_DEACTIVATION_SNACKBAR);
          return;
        }
        const profileUrl = getProfileRedirectUrl(profileRedirectUrl);

        if (!isServer() && globalThis.location && profileUrl) {
          globalThis.location.href = profileUrl;
          return;
        }
      }

      if (isSuccessfulDeactivation) {
        forceLogout();
        return;
      }

      setCurrentStage(DEACTIVATION_STAGES.CONFIRM);
    } catch (error: unknown) {
      publishMilestone(false);
      setCurrentStage(DEACTIVATION_STAGES.ERROR);
    }
  }, [
    deactivationReason,
    dispatch,
    effectiveCancellationType,
    isBasicSubscription,
    isIdentityPlusSubscription,
    isCoolingOffEligible,
    cancellationType,
    profileRedirectUrl,
    forceLogout,
    nextBillingDate,
    isWebview,
    deactivateFlexUser,
    useFlexFlow,
  ]);

  const renderStage = () => {
    switch (currentStage) {
      case DEACTIVATION_STAGES.LOADING:
        return <LoadingStage loadingMessage={loadingMessage} isWebview={isWebview} />;

      case DEACTIVATION_STAGES.REASON:
        return (
          <ContentCard $isWebview={isWebview} $tabletMarginTop="32px" $noPaddingBottom={isWebview}>
            <ReasonCaptureStage
              onContinue={handleReasonContinue}
              onBack={handleBackToPersonalDetails}
              isPaidUser={!isBasicSubscription}
              isDeferred={isDeferred || cancellationType === CancellationType.Deferred}
              productDisplayName={productDisplayName}
              formattedNextBillingDate={formattedNextBillingDate}
              isWebview={isWebview}
            />
          </ContentCard>
        );

      case DEACTIVATION_STAGES.CHOICE:
        return (
          <ContentCard $isWebview={isWebview}>
            <ChoiceCancelStage
              subscriptionFamily={currentSubscription?.family || ''}
              productDisplayName={productDisplayName}
              formattedNextBillingDate={formattedNextBillingDate}
              product={product}
              onSelectDeferred={handleSelectDeferred}
              onSelectImmediate={handleSelectImmediate}
              onMySubscriptionClick={handleMySubscriptionClick}
            />
          </ContentCard>
        );

      case DEACTIVATION_STAGES.CONFIRM:
        return (
          <ContentCard $isWebview={isWebview}>
            <ConfirmCancelStage
              product={product}
              planType={planType}
              cancellationType={effectiveCancellationType}
              formattedNextBillingDate={formattedNextBillingDate}
              isCoolingOffEligible={!!isCoolingOffEligible}
              onConfirm={handleConfirmDeactivation}
              isWebview={isWebview}
              subscriptionFamily={currentSubscription?.family}
              productDisplayName={productDisplayName}
              onMySubscriptionClick={handleMySubscriptionClick}
            />
          </ContentCard>
        );

      case DEACTIVATION_STAGES.ERROR:
        return (
          <ErrorScreen
            title="We could not deactivate your account"
            subtitle="It was an issue at our end. You have not been charged."
            onTryAgain={handleBackToPersonalDetails}
          />
        );
    }
  };

  const showBackButton =
    !isWebview &&
    currentStage !== DEACTIVATION_STAGES.LOADING &&
    currentStage !== DEACTIVATION_STAGES.REASON &&
    currentStage !== DEACTIVATION_STAGES.ERROR;

  return (
    <ThemeProvider theme={nebulaTheme}>
      <DeactivateWrapper $isWebview={isWebview}>
        <DeactivateContainer $isWebview={isWebview}>
          <DeactivateContentContainer>
            {showBackButton && (
              <BackLinkWrapper $isWebview={isWebview} data-back-link="true" tabIndex={0} onClick={handleBackClick}>
                Back
              </BackLinkWrapper>
            )}
            {renderStage()}
          </DeactivateContentContainer>
        </DeactivateContainer>
      </DeactivateWrapper>
    </ThemeProvider>
  );
};

/*
  The deactivation stage and cancellation type are derived once, when the stages mount. In the flex flow those
  are driven by the entitlements response, so the stages must not mount until it has loaded - otherwise
  isInCoolingOffPeriod reads as false and the user is sent straight to the reason stage, skipping the choice
  between cancelling immediately and at the end of the billing period.
*/
const DeactivateOrchestrator: FC<DeactivateOrchestratorProps> = ({ isWebview = false, useFlexFlow = false }) => {
  const { isLoading, isUninitialized } = useGetEntitlementsQuery(undefined);

  if (useFlexFlow && (isUninitialized || isLoading)) {
    return (
      <FullPageContainer data-testid="deactivate-loading">
        <AnimatedLogo />
      </FullPageContainer>
    );
  }

  return <DeactivateStages isWebview={isWebview} useFlexFlow={useFlexFlow} />;
};

export default DeactivateOrchestrator;
